import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { motion, AnimatePresence } from 'framer-motion';
import Dither from './Dither';

const SpecRow = ({ label, title, description, isLast, index }) => (
    <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
        className={`col-span-12 grid grid-cols-1 md:grid-cols-12 ${!isLast ? 'border-b-brut' : ''}`}
    >
        
        {/* Label Column */}
        <div className="col-span-12 md:col-span-2 p-6 md:p-8 border-b-brut md:border-b-0 md:border-r-brut flex items-start">
            <span className="font-mono text-[10px] text-muted uppercase tracking-widest">{label}</span>
        </div>
        
        {/* Title Column */}
        <div className="col-span-12 md:col-span-4 p-6 md:p-8 border-b-brut md:border-b-0 md:border-r-brut flex items-center">
            <h3 className="text-3xl md:text-5xl font-heading uppercase leading-none tracking-tight">
                {title}
            </h3>
        </div>
        
        {/* Description Column */}
        <div className="col-span-12 md:col-span-4 p-6 md:p-8 border-b-brut md:border-b-0 md:border-r-brut flex items-center">
            <p className="font-mono text-xs text-foreground uppercase leading-relaxed max-w-sm">
                {description}
            </p>
        </div>

        {/* Action/Visual Column */}
        <a href="#contact" className="col-span-12 md:col-span-2 p-6 flex items-center justify-center relative group cursor-pointer overflow-hidden block">
            <div className="absolute inset-0 bg-accent/5 backdrop-blur-sm transform origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] z-0" />
            
            <div className="relative z-10 flex items-center justify-center w-full h-full py-2">
                <div 
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-border/50 bg-background/50 backdrop-blur-md flex items-center justify-center group-hover:w-[120%] group-hover:rounded-full group-hover:bg-accent group-hover:border-accent group-hover:shadow-[0_0_40px_rgba(56,189,248,0.4)] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] overflow-hidden relative"
                >
                    {/* The arrow that shifts right and disappears */}
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-foreground absolute group-hover:translate-x-32 group-hover:opacity-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>

                    {/* The text that slides in from the left on hover */}
                    <span className="font-heading text-2xl md:text-3xl font-extrabold uppercase text-background tracking-tighter absolute -translate-x-32 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-700 delay-100 ease-[cubic-bezier(0.16,1,0.3,1)] whitespace-nowrap">
                        {translations[useLanguage().language].cap_engage}
                    </span>
                </div>
            </div>
        </a>

    </motion.div>
);

export default function Capabilities() {
    const { language } = useLanguage();
    const t = translations[language];
    const [activeTab, setActiveTab] = useState('SERVICES');

    const dataArrays = {
        'SERVICES': [
            { label: t.cap_srv_proto_lbl, title: t.cap_srv_proto_tit, desc: t.cap_srv_proto_desc },
            { label: t.cap_srv_int_lbl, title: t.cap_srv_int_tit, desc: t.cap_srv_int_desc },
            { label: t.cap_srv_hard_lbl, title: t.cap_srv_hard_tit, desc: t.cap_srv_hard_desc },
        ],
        'TECHNOLOGY': [
            { label: t.cap_tech_front_lbl, title: t.cap_tech_front_tit, desc: t.cap_tech_front_desc },
            { label: t.cap_tech_infra_lbl, title: t.cap_tech_infra_tit, desc: t.cap_tech_infra_desc },
            { label: t.cap_tech_db_lbl, title: t.cap_tech_db_tit, desc: t.cap_tech_db_desc },
        ]
    };

    const currentData = dataArrays[activeTab];

    return (
        <section id="capabilities" className="w-full bg-background grid-sys min-h-[70vh] relative overflow-hidden z-0">
            
            {/* Dither Shape Window */}
            <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[120vw] h-[120vw] md:w-[40vw] md:h-[40vw] rounded-full overflow-hidden z-0 opacity-80 border border-white/5 shadow-[0_0_50px_rgba(56,189,248,0.15)] pointer-events-auto transition-all duration-700">
                <Dither
                    waveColor={[56/255, 189/255, 248/255]} 
                    disableAnimation={false}
                    enableMouseInteraction
                    mouseRadius={0.3}
                    colorNum={4}
                    waveAmplitude={0.3}
                    waveFrequency={3}
                    waveSpeed={0.05}
                />
            </div>

            {/* Visionary Massive Text */}
            <div className="col-span-12 p-6 md:p-16 border-b-brut min-h-[60vh] flex flex-col justify-center relative z-10">
                <motion.h2 
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="text-4xl md:text-5xl lg:text-7xl font-heading font-extrabold uppercase leading-[0.95] tracking-tighter text-foreground max-w-[90rem]"
                >
                    {t.cap_vision}
                </motion.h2>
                <motion.a 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                    href="#contact"
                    className="mt-12 w-fit border border-foreground py-3 px-8 rounded-full uppercase text-xs tracking-widest font-mono hover:bg-foreground hover:text-background transition-all cursor-pointer"
                >
                    {t.cap_partner}
                </motion.a>
            </div>
            
            {/* Section Header */}
            <div className="col-span-12 flex justify-between items-center p-6 md:p-8 border-b-brut bg-black/40 backdrop-blur-md relative z-10">
                <div className="flex gap-12">
                     <button 
                        onClick={() => setActiveTab('SERVICES')}
                        className={`font-mono text-xs font-bold tracking-widest uppercase transition-colors ${activeTab === 'SERVICES' ? 'text-accent hover:underline' : 'text-muted hover:text-foreground'}`}
                    >
                        {t.cap_tab_services}
                    </button>
                     <button 
                        onClick={() => setActiveTab('TECHNOLOGY')}
                        className={`font-mono text-xs font-bold tracking-widest uppercase transition-colors ${activeTab === 'TECHNOLOGY' ? 'text-accent hover:underline' : 'text-muted hover:text-foreground'}`}
                    >
                        {t.cap_tab_technology}
                    </button>
                </div>
                
                <span className="font-mono text-[10px] text-muted tracking-widest uppercase hidden md:block">
                    {t.cap_spec}
                </span>
            </div>

            {/* Specification Rows */}
            <div className="col-span-12 bg-black/20 backdrop-blur-sm relative z-10">
                <AnimatePresence mode="wait">
                    <motion.div key={activeTab} className="w-full">
                        {currentData.map((srv, index) => (
                            <SpecRow 
                                key={`${activeTab}-${index}`}
                                index={index}
                                label={srv.label}
                                title={srv.title}
                                description={srv.desc}
                                isLast={index === currentData.length - 1}
                            />
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>

        </section>
    );
}
